import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { chakraExtendedTheme } from '@/styles/theme';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import { supabase } from '@/lib/supabase';
import seoKeywordsService from '@/services/seoKeywords';
import searchConsoleService, { GoogleConnectionStatus } from '@/services/searchConsole';
import { SeoTrackedKeyword } from '@/types/api/seo';
import AddKeywordsModal from '@/components/AddKeywordsModal';
import SeoKeywordListComponent from './SeoKeywordList.component';

type KeywordsPaginated = { data: SeoTrackedKeyword[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoKeywordListPage = function (): JSX.Element {
  const navigate = useNavigate();
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';
  const siteUrl = project?.siteUrl ?? null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importCount, setImportCount] = useState<number | null>(null);

  const { data: keywordsData, isLoading: isLoadingKeywords, refetch: refetchKeywords } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId, retry: false }
  );

  const { data: connectionStatus } = useSeoFetch<GoogleConnectionStatus>(
    [FetchApiIds.SEO_GOOGLE_CONNECTION],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = !!connectionStatus?.isConnected;

  const keywords = useMemo(
    () => keywordsData?.data ?? [],
    [keywordsData]
  );

  // Fetch copilot sessions to show which keywords have active copilot
  type CopilotSessionRow = { keyword_id: string; status: string };
  const { data: copilotSessions = [] } = useSeoFetch<CopilotSessionRow[]>(
    ['seo-copilot-sessions', projectId],
    async () => {
      const { data, error } = await supabase
        .from('seo_copilot_sessions')
        .select('keyword_id, status')
        .eq('project_id', projectId)
        .in('status', ['active', 'paused']);
      if (error) throw error;
      return data ?? [];
    },
    { enabled: !!projectId, retry: false }
  );

  const copilotStatusMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of copilotSessions) {
      map.set(s.keyword_id, s.status);
    }
    return map;
  }, [copilotSessions]);

  const handleImportFromGSC = useCallback(async () => {
    if (!siteUrl || !projectId) return;
    setIsImporting(true);
    setImportCount(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      // Fetch keywords with page mapping from GSC
      const gscKeywords = await searchConsoleService.getKeywordsWithPages(siteUrl, 28, 500);

      if (gscKeywords.length === 0) {
        setImportCount(0);
        return;
      }

      // Bulk import with snapshots
      await seoKeywordsService.bulkImportFromGSC({
        projectId,
        createdBy: user?.id ?? '',
        keywords: gscKeywords.map((kw) => ({
          keyword: kw.keyword,
          page: kw.page,
          position: kw.position,
          impressions: kw.impressions,
          clicks: kw.clicks,
        })),
      });

      setImportCount(gscKeywords.length);
      refetchKeywords();
    } catch (err) {
      console.error('[SEOx] GSC import failed:', err);
    } finally {
      setIsImporting(false);
    }
  }, [siteUrl, projectId, refetchKeywords]);

  const handleDeleteKeywords = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    await seoKeywordsService.bulkDelete(ids);
    refetchKeywords();
  }, [refetchKeywords]);

  const handleAddKeyword = useCallback(async (newKeyword: string) => {
    if (!newKeyword || !projectId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // 1. Create keyword row
      const created = await seoKeywordsService.bulkCreate({
        projectId,
        keywords: [newKeyword],
        createdBy: user?.id ?? '',
      });

      const createdId = created[0]?.shortId;

      // 2. If GSC is connected, try to fetch initial position (non-blocking)
      if (siteUrl && isConnected) {
        try {
          const gscData = await searchConsoleService.getKeywordsWithPages(siteUrl, 90, 1000);
          const match = gscData.find((row) => row.keyword.toLowerCase() === newKeyword.trim().toLowerCase());

          if (match) {
            await seoKeywordsService.bulkImportFromGSC({
              projectId,
              createdBy: user?.id ?? '',
              keywords: [{
                keyword: newKeyword.trim(),
                page: match.page,
                position: match.position,
                impressions: match.impressions,
                clicks: match.clicks,
              }],
            });
          }
        } catch (err) {
          console.warn('[SEOx] Failed to fetch initial position:', err);
        }
      }

      setIsModalOpen(false);

      // Navigate to keyword detail page
      if (createdId) {
        navigate(`/seox/projects/${projectId}/keywords/${createdId}`);
      } else {
        refetchKeywords();
      }
    } catch (err) {
      console.error('[SEOx] Failed to add keyword:', err);
    }
  }, [projectId, siteUrl, isConnected, refetchKeywords, navigate]);

  return (
    <>
      <SeoKeywordListComponent
        keywords={keywords}
        copilotStatusMap={copilotStatusMap}
        isLoading={isLoadingKeywords && !!projectId}
        onAddKeywords={() => setIsModalOpen(true)}
        onImportFromGSC={handleImportFromGSC}
        onDeleteKeywords={handleDeleteKeywords}
        isImporting={isImporting}
        importCount={importCount}
        canImport={isConnected && !!siteUrl}
      />
      <ChakraProvider theme={chakraExtendedTheme}>
        <AddKeywordsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleAddKeyword}
        />
      </ChakraProvider>
    </>
  );
};

export default SeoKeywordListPage;
